<template>
  <div class="space-y-6">
    <!-- Completion Summary -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div class="flex items-center gap-4 mb-4">
        <div class="relative h-14 w-14">
          <svg class="h-14 w-14 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke-width="3"
              class="stroke-gray-200 dark:stroke-gray-700" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke-width="3"
              class="stroke-primary-500"
              stroke-dasharray="100"
              :stroke-dashoffset="100 - completionPct"
              stroke-linecap="round" />
          </svg>
          <span class="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-900 dark:text-gray-100">
            {{ completionPct }}%
          </span>
        </div>
        <div>
          <h3 class="text-lg font-medium font-poppins text-gray-900 dark:text-gray-100">
            Review & Submit
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ completionPct === 100 ? 'All sections complete.' : 'Some sections may still need attention.' }}
          </p>
        </div>
      </div>

      <div v-if="incompleteSteps.length > 0" class="mt-4">
        <p class="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">Incomplete steps:</p>
        <ul class="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li v-for="step in incompleteSteps" :key="step">{{ step }}</li>
        </ul>
      </div>
    </div>

    <!-- Business Info -->
    <ReviewSection title="Business Information">
      <ReviewItem label="Business Name" :value="o.business_name" />
      <ReviewItem label="Website" :value="o.business_website_url" />
      <ReviewItem label="Industry" :value="o.industry" />
      <ReviewItem label="Primary Service" :value="o.primary_service_offering" />
      <ReviewItem label="Contact Name" :value="o.contact_name" />
      <ReviewItem label="Contact Email" :value="o.contact_email" />
      <ReviewItem label="Contact Phone" :value="o.contact_phone" />
      <ReviewItem label="Billing Contact" :value="o.billing_contact_name" />
      <ReviewItem label="Billing Email" :value="o.billing_contact_email" />
      <ReviewItem label="Billing Phone" :value="o.billing_contact_phone" />
    </ReviewSection>

    <!-- Services -->
    <ReviewSection title="Services">
      <ReviewItem label="Selected Services" :value="serviceLabels" />
    </ReviewSection>

    <!-- Integrations -->
    <ReviewSection title="Integrations">
      <ReviewItem label="Google Analytics 4" :value="o.has_ga4" />
      <ReviewItem label="Google Tag Manager" :value="o.has_gtm" />
      <ReviewItem label="Call Tracking" :value="o.call_tracking_provider" />
      <ReviewItem label="CRM" :value="o.crm_provider" />
    </ReviewSection>

    <!-- SEO Details -->
    <ReviewSection v-if="hasSeo" title="SEO Details">
      <ReviewItem label="GBP Access" :value="seo.gbp_access" />
      <ReviewItem label="GA4 Access" :value="seo.ga4_access" />
      <ReviewItem label="GSC Access" :value="seo.gsc_access" />
      <ReviewItem label="Website Platform" :value="seo.website_platform" />
      <ReviewItem label="Nameserver" :value="seo.nameserver" />
      <ReviewItem label="GF Hosting" :value="seo.gf_hosting" />
      <ReviewItem label="Host Name" :value="seo.host_name" />
      <ReviewItem label="Call Frequency" :value="seo.call_frequency" />
      <ReviewItem label="Locations" :value="seo.num_locations" />
      <ReviewItem label="Client Spend" :value="seo.client_spend ? '$' + seo.client_spend : null" />
      <ReviewItem label="Closing Ratio" :value="seo.closing_ratio ? seo.closing_ratio + '%' : null" />
      <ReviewItem label="Avg Ticket / LTV" :value="seo.avg_ticket ? '$' + seo.avg_ticket : null" />
      <ReviewItem label="Services Offered" :value="seo.services_offered" />
      <ReviewItem label="Services Not Offered" :value="seo.services_not_offered" />
      <ReviewItem label="Citation Phone" :value="seo.citation_phone" />
      <ReviewItem label="Citation Email" :value="seo.citation_email" />
    </ReviewSection>

    <!-- PPC Details -->
    <ReviewSection v-if="hasPpc" title="PPC Details">
      <ReviewItem label="Google Ads ID" :value="ppc.google_ads_account_id" />
      <ReviewItem label="Login Email" :value="ppc.google_ads_login_email" />
      <ReviewItem label="Monthly Budget" :value="ppc.monthly_ad_budget ? '$' + ppc.monthly_ad_budget : null" />
      <ReviewItem label="Target Area" :value="ppc.target_geo" />
      <ReviewItem label="Primary Goal" :value="ppc.primary_goal" />
      <ReviewItem label="Landing Pages" :value="ppc.landing_pages" />
      <ReviewItem label="Target CPA/ROAS" :value="ppc.target_cpa_roas" />
      <ReviewItem label="Volume Target" :value="ppc.monthly_volume_target" />
    </ReviewSection>

    <!-- Internal Notes -->
    <ReviewSection v-if="mode === 'internal'" title="Internal Notes">
      <ReviewItem label="Account Lead" :value="o.account_lead_name" />
      <ReviewItem label="Priority" :value="o.onboarding_priority" />
      <ReviewItem label="Contract Start" :value="o.contract_start_date" />
      <ReviewItem label="Sales Notes" :value="o.notes_from_sales" />
    </ReviewSection>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import ReviewSection from './review/review_section.vue'
import ReviewItem from './review/review_item.vue'

const props = defineProps({
  stepKey: { type: String, required: true },
  label: { type: String, required: true },
  onboarding: { type: Object, required: true },
  mode: { type: String, required: true }
})

const o = computed(() => props.onboarding)
const seo = computed(() => o.value.seo_detail || {})
const ppc = computed(() => o.value.ppc_detail || {})

const completionPct = computed(() => o.value.completion_percentage || 0)

const hasSeo = computed(() => (o.value.service_types || []).includes('seo'))
const hasPpc = computed(() => {
  const types = o.value.service_types || []
  return types.includes('ppc_adwords') || types.includes('ppc_lsa')
})

const serviceLabels = computed(() => {
  const map = { seo: 'SEO', ppc_adwords: 'PPC - Google Ads', ppc_lsa: 'PPC - Local Service Ads' }
  return (o.value.service_types || []).map(s => map[s] || s).join(', ') || null
})

const incompleteSteps = computed(() => {
  const progress = o.value.progress || {}
  const defs = o.value.step_definitions || []
  return defs
    .filter(s => !progress[s.key])
    .map(s => s.label)
})

defineExpose({
  getData: () => ({})
})
</script>
